// Gruppierung der Seite Verkäufe als reine Funktionen, ohne Vue und DevExtreme (Kundenwunsch 12.10.2026, wörtlich:
// „grupierung zudem frei wählbar machen nicht nur die vorgegebenen also auch eine art menü zur auswahl der
// gruppierung“). Bis zu drei Ebenen aus GROUP_FIELDS; die bisherigen festen Kombinationen sind Vorlagen
// (GROUP_PRESETS). Gruppiert wird im Browser über verborgene Hilfsspalten (groupColumns): Gruppenschlüssel aus
// calculateGroupValue, lesbarer Wert in Gruppenzeile und Excel über customizeText, Reihenfolge über sortingMethod.
// Nichts davon wird gespeichert.
//
// Ebenen = Array von Feldschlüsseln, z. B. ['fahrer', 'tag']. Gruppenschlüssel sind Texte bzw. beim Preis Zahlen;
// '' steht für ohne Angabe (null, undefined, leer) und steht am Ende.
import moment from 'moment';
import { formatEuro, formatMonth } from '@/utils/format';
import { NO_VALUE_TEXT, valueKey, foldText, sortDateOrder, normalizeSortSpec, tariffZones } from './saleFilters';
import { zahlart, zahlartText } from './zahlart';

export const MAX_LEVELS = 3;

// Deutsch mit Zahlen in natürlicher Folge („12“ vor „100“, „N2“ vor „N10“), ohne Groß-/Kleinschreibung
const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });
// Linie und Fahrt im Schlüssel der Ebene „Fahrt“ (Fahrtnummern können sich zwischen Linien wiederholen)
const SEP = '\u001f';

function compareText(a, b) {
  const x = valueKey(a);
  const y = valueKey(b);
  if ((x === '') !== (y === '')) {
    return x === '' ? 1 : -1;
  }
  return collator.compare(x, y) || (x < y ? -1 : x > y ? 1 : 0);
}

function compareNumber(a, b) {
  const x = a === '' || a === null || a === undefined ? NaN : Number(a);
  const y = b === '' || b === null || b === undefined ? NaN : Number(b);
  if (isNaN(x) !== isNaN(y)) {
    return isNaN(x) ? 1 : -1;
  }
  return isNaN(x) ? 0 : x - y;
}

function rawKey(field) {
  return (row) => valueKey(row[field]);
}

function plainText(key) {
  return valueKey(key) === '' ? NO_VALUE_TEXT : String(key);
}

// Verkaufstag und -monat in Ortszeit wie die Spalte „Verkauft am“
function saleMoment(row) {
  const m = row && row.sale_time ? moment(row.sale_time) : null;
  return m && m.isValid() ? m : null;
}

function fahrtParts(key) {
  const parts = String(key).split(SEP);
  return { line: parts[0] || '', trip: parts[1] || '' };
}

// Ebene „Fahrt“: unter einer Ebene „Linie“ nur die Fahrtnummer (wie die bisherige Vorlage „Linie/Fahrt“), sonst mit
// Linie, damit gleiche Fahrtnummern verschiedener Linien unterscheidbar bleiben
function fahrtText(key, ctx) {
  const { line, trip } = fahrtParts(key);
  const levels = ctx && typeof ctx.levels === 'function' ? ctx.levels() : [];
  const own = levels.indexOf('fahrt');
  const lineLevel = levels.indexOf('linie');
  const tripText = trip === '' ? NO_VALUE_TEXT : trip;
  if (lineLevel !== -1 && own !== -1 && lineLevel < own) {
    return tripText;
  }
  return tripText + ' (' + (line === '' ? 'ohne Linie' : 'Linie ' + line) + ')';
}

function compareFahrt(a, b) {
  const x = fahrtParts(a);
  const y = fahrtParts(b);
  return compareText(x.line, y.line) || compareText(x.trip, y.trip);
}

const DIRECTION_RANK = { hin: 0, rueck: 1, '': 2 };

// key: Schlüssel der Ebene; label: Feldname (Menü, Knopf, Gruppenzeile „Fahrer: Anna Berger“); field: Rohfeld
// (dataField der Hilfsspalte); value(row): Gruppenschlüssel; text(key, ctx): lesbarer Wert; compare(a, b, ctx):
// Reihenfolge der Gruppen (aufsteigend); follows: Sortierspalte, deren Richtung die Gruppen übernehmen ('date' =
// Richtung der Verkaufszeit, sonst aufsteigend). Reihenfolge und Trennlinien (group) wie im Menü.
export const GROUP_FIELDS = [
  {
    key: 'tag',
    label: 'Datum',
    field: 'sale_time',
    group: 'zeit',
    value: (row) => {
      const m = saleMoment(row);
      return m ? m.format('YYYY-MM-DD') : '';
    },
    text: (key) => (key ? moment(key, 'YYYY-MM-DD').format('DD.MM.YYYY') : NO_VALUE_TEXT),
    compare: compareText,
    follows: 'date'
  },
  {
    key: 'monat',
    label: 'Monat',
    field: 'sale_time',
    group: 'zeit',
    value: (row) => {
      const m = saleMoment(row);
      return m ? m.format('YYYY-MM') : '';
    },
    text: (key) => (key ? formatMonth(key) : NO_VALUE_TEXT),
    compare: compareText,
    follows: 'date'
  },
  {
    key: 'fahrer',
    label: 'Fahrer',
    field: 'username',
    group: 'personal',
    value: rawKey('username'),
    // voller Name statt Login; nach dem Namen geordnet wie „Sortierung: Fahrer · A–Z“, gleiche Namen nach dem Login
    text: (key, ctx) => (key === '' ? NO_VALUE_TEXT : (ctx && ctx.driverName(key)) || key),
    compare: (a, b, ctx) => {
      const name = (key) => (key === '' ? '' : foldText((ctx && ctx.driverName(key)) || key));
      return compareText(name(a), name(b)) || compareText(a, b);
    },
    follows: 'username'
  },
  {
    key: 'geraet',
    label: 'Gerät',
    field: 'printer_id',
    group: 'personal',
    value: rawKey('printer_id'),
    text: plainText,
    compare: compareText,
    follows: 'printer_id'
  },
  {
    key: 'linie',
    label: 'Linie',
    field: 'line_id',
    group: 'fahrt',
    value: rawKey('line_id'),
    text: plainText,
    compare: compareText,
    follows: 'line_id'
  },
  {
    key: 'fahrt',
    label: 'Fahrt',
    field: 'trip_id',
    group: 'fahrt',
    value: (row) => valueKey(row.line_id) + SEP + valueKey(row.trip_id),
    text: fahrtText,
    compare: compareFahrt,
    follows: 'line_id'
  },
  {
    key: 'richtung',
    label: 'Richtung',
    field: 'is_outward_direction',
    group: 'fahrt',
    // wie das Verkaufsdetail: 1 = Hinfahrt, jeder andere vorhandene Wert = Rückfahrt
    value: (row) => {
      const v = valueKey(row.is_outward_direction);
      return v === '' ? '' : v === '1' ? 'hin' : 'rueck';
    },
    text: (key) => (key === 'hin' ? 'Hinfahrt' : key === 'rueck' ? 'Rückfahrt' : NO_VALUE_TEXT),
    compare: (a, b) => DIRECTION_RANK[valueKey(a)] - DIRECTION_RANK[valueKey(b)]
  },
  {
    key: 'von',
    label: 'Von',
    field: 'location_from_name',
    group: 'fahrt',
    value: rawKey('location_from_name'),
    text: plainText,
    compare: compareText,
    follows: 'location_from_name'
  },
  {
    key: 'nach',
    label: 'Nach',
    field: 'location_to_name',
    group: 'fahrt',
    value: rawKey('location_to_name'),
    text: plainText,
    compare: compareText,
    follows: 'location_to_name'
  },
  {
    key: 'ticket',
    label: 'Ticket',
    field: 'ticket_name',
    group: 'ticket',
    value: rawKey('ticket_name'),
    text: plainText,
    compare: compareText,
    follows: 'ticket_strecke'
  },
  {
    key: 'preis',
    label: 'Preis',
    field: 'price',
    group: 'ticket',
    value: (row) => {
      const v = valueKey(row.price) === '' ? NaN : Number(row.price);
      return isFinite(v) ? v : '';
    },
    text: (key) => (key === '' || key === null || key === undefined ? NO_VALUE_TEXT : formatEuro(key)),
    compare: compareNumber,
    follows: 'price'
  },
  {
    key: 'preisstufe',
    label: 'Preisstufe',
    field: 'price_level',
    group: 'ticket',
    value: rawKey('price_level'),
    text: plainText,
    compare: compareText
  },
  {
    key: 'tarif',
    label: 'Tarif',
    field: 'tariff_name',
    group: 'ticket',
    value: rawKey('tariff_name'),
    text: plainText,
    compare: compareText
  },
  {
    key: 'tarifkombi',
    // kurz statt „Tarif-Kombination“ (Spalte, bisherige Vorlage): „Fahrer › Linie › Tarifzonen“ passt auf den Knopf
    label: 'Tarifzonen',
    field: 'tariff_zone_combination',
    group: 'ticket',
    // wie die Spalte „Tarif-Kombination“ und die Filterliste „Tarifzonen“ (tariffZones in saleFilters.js)
    value: tariffZones,
    text: plainText,
    compare: compareText
  },
  {
    key: 'zahlart',
    label: 'Zahlart',
    field: 'payment_method',
    group: 'ticket',
    // wie Spalte, Filter und Detail: Bar, Karte (auch externes Terminal) oder Gutschein (zahlart.js)
    value: (row) => zahlart(row),
    text: (key) => zahlartText(key),
    compare: compareText,
    follows: 'payment_method'
  },
  {
    key: 'mandant',
    label: 'Mandant',
    field: 'logo_suffix',
    group: 'mandant',
    value: rawKey('logo_suffix'),
    text: plainText,
    compare: compareText
  }
];

const FIELD_BY_KEY = GROUP_FIELDS.reduce((all, field) => {
  all[field.key] = field;
  return all;
}, {});

// Name der Hilfsspalte einer Ebene im Grid
export function groupColumnName(key) {
  return 'gruppe_' + key;
}

export const GROUP_COLUMN_NAMES = GROUP_FIELDS.map((field) => groupColumnName(field.key));

// Vorlagen = die bisherigen festen Gruppierungen (Werte wie bisher) mit denselben Ebenen in derselben Folge; „Fahrer/
// Gerät“ gruppierte bisher nach der Fahrer-ID (gleiche Gruppen, solange ID und Login zusammengehören)
export const GROUP_PRESETS = [
  { value: 'line', levels: ['linie', 'fahrt'] },
  { value: 'user', levels: ['fahrer', 'tag'] },
  { value: 'device', levels: ['geraet', 'tag'] },
  { value: 'tickettype', levels: ['ticket', 'preis'] },
  { value: 'driverprinter', levels: ['fahrer', 'geraet'] },
  { value: 'datetype', levels: ['tag'] },
  { value: 'driverlinetariff', levels: ['fahrer', 'linie', 'tarifkombi'] },
  { value: 'tariff_zone_combination', levels: ['tarifkombi'] },
  { value: 'drivermandanttariff', levels: ['fahrer', 'mandant', 'tarif'] }
].map((preset) => Object.assign(preset, { text: levelsText(preset.levels) }));

// gültige Ebenen: bekannte Felder, jedes höchstens einmal, höchstens drei
export function normalizeLevels(levels) {
  const result = [];
  (Array.isArray(levels) ? levels : []).forEach((key) => {
    if (FIELD_BY_KEY[key] && result.indexOf(key) === -1 && result.length < MAX_LEVELS) {
      result.push(key);
    }
  });
  return result;
}

export function sameLevels(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((key, i) => key === b[i]);
}

function levelsText(levels) {
  return levels.map((key) => FIELD_BY_KEY[key].label).join(' › ');
}

// Knopf: „Fahrer › Datum“ bzw. „Gruppierung“ (seit 13.10.2026 wie „Filter“ ohne Filter); Tooltip mit Pfeil („Gruppierung: Fahrer › Datum“). Der zugängliche
// Name enthält den sichtbaren Text in derselben Folge (WCAG 2.5.3, Sprachsteuerung „Klick Keine Gruppierung“): „Keine
// Gruppierung“ bzw. „Gruppierung: Fahrer, Datum“ (ohne Pfeilzeichen)
export function describeGrouping(levels) {
  const keys = normalizeLevels(levels);
  if (!keys.length) {
    // ohne Gruppierung nur „Gruppierung“, wie „Filter“ ohne Filter (Kundenwunsch 13.10.2026, für alle Seiten)
    return { text: 'Gruppierung', name: 'Gruppierung: keine', title: 'Gruppierung: keine' };
  }
  const labels = keys.map((key) => FIELD_BY_KEY[key].label);
  return {
    text: labels.join(' › '),
    name: 'Gruppierung: ' + labels.join(', '),
    title: 'Gruppierung: ' + labels.join(' › ')
  };
}

// Felder mit mindestens zwei Werten in den geladenen Zeilen (nur sie ergeben mehr als eine Gruppe)
export function availableGroupFields(rows) {
  const data = Array.isArray(rows) ? rows : [];
  return GROUP_FIELDS.filter((field) => {
    let seen = false;
    let first = null;
    for (let i = 0; i < data.length; i++) {
      if (data[i]) {
        const v = field.value(data[i]);
        if (!seen) {
          seen = true;
          first = v;
        } else if (v !== first) {
          return true;
        }
      }
    }
    return false;
  }).map((field) => field.key);
}

// Einträge einer Ebenen-Auswahl (AppSelect): verfügbare Felder ohne die der übrigen Ebenen, das eigene Feld immer;
// Trennlinien zwischen Zeit, Personal und Gerät, Fahrt, Ticket und Mandant
export function levelItems(levels, available, own) {
  const used = (levels || []).filter((key) => key !== own);
  const items = [];
  let lastGroup = null;
  GROUP_FIELDS.forEach((field) => {
    const free = (available || []).indexOf(field.key) !== -1 && used.indexOf(field.key) === -1;
    const allowed = field.key === own || free;
    if (!allowed) {
      return;
    }
    if (lastGroup !== null && field.group !== lastGroup) {
      items.push({ divider: true });
    }
    lastGroup = field.group;
    items.push({ value: field.key, text: field.label });
  });
  return items;
}

// Richtung der Gruppen einer Ebene: Datum und Monat wie die Verkaufszeit (neueste zuerst, außer „Älteste zuerst“),
// sonst wie die Sortierung derselben Spalte (z. B. „Preis · höchster zuerst“ ordnet die Preisgruppen absteigend),
// ohne passende Sortierung aufsteigend
export function groupOrder(key, spec) {
  const field = FIELD_BY_KEY[key];
  if (!field) {
    return 'asc';
  }
  if (field.follows === 'date') {
    return sortDateOrder(spec);
  }
  const entry = field.follows ? normalizeSortSpec(spec).find((item) => item[0] === field.follows) : null;
  return entry ? entry[1] : 'asc';
}

// Hilfsspalten für das Grid, je Feld eine; immer verborgen, nicht im Export, nicht in der Suche. ctx.driverName(login)
// liefert den vollen Namen, ctx.levels() die aktuellen Ebenen (beide zur Laufzeit gelesen).
export function groupColumns(ctx) {
  return GROUP_FIELDS.map((field) => ({
    name: groupColumnName(field.key),
    dataField: field.field,
    caption: field.label,
    dataType: 'string',
    visible: false,
    allowExporting: false,
    allowSearch: false,
    calculateGroupValue: (row) => field.value(row),
    customizeText: (info) => field.text(info.value, ctx),
    sortingMethod: (a, b) => field.compare(a, b, ctx)
  }));
}
